import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '../logo.jpg';

export const SupportPage: React.FC = () => {
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const modules = [
        "Performance Intelligence",
        "Order Intelligence",
        "Customer Hub",
        "Product Intelligence",
        "Advanced Segmentation",
        "Intelligence Center",
        "Reporting & Forecasting",
        "Store Data Synchronization"
    ];

    const contactReasons = [
        "Technical support",
        "Dashboard assistance",
        "Store sync issues",
        "Analytics setup",
        "Export and reporting help",
        "Feature guidance",
        "Account configuration",
        "Billing-related questions",
        "Integration support",
        "General product assistance"
    ];

    return (
        <div style={{
            minHeight: '100vh',
            fontFamily: "'Outfit', 'Inter', system-ui, -apple-system, sans-serif",
            color: '#0f172a',
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 40%, #f5f3ff 70%, #faf5ff 100%)',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <header className="landing-header" style={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(241, 245, 249, 0.8)',
                padding: '1.25rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)'
            }}>
                <div 
                    className="header-logo" 
                    onClick={() => navigate('/')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer' }}
                >
                    <img 
                        src={logoImg} 
                        alt="Tvora Logo" 
                        style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '10px',
                            objectFit: 'cover',
                            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)'
                        }} 
                    />
                    <div>
                        <span className="logo-title" style={{
                            fontWeight: 800,
                            fontSize: '1.45rem',
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #1e293b 0%, #6366f1 100%)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Tvora
                        </span>
                        <span className="logo-subtitle" style={{
                            display: 'block',
                            fontSize: '0.65rem',
                            color: '#64748b',
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            marginTop: '-2px'
                        }}>
                            Analytics & Profit
                        </span>
                    </div>
                </div>

                <nav style={{ display: 'flex', gap: '2rem', alignItems: 'center' }} className="nav-desktop">
                    <a href="/#features" onClick={() => navigate('/#features')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Features</a>
                    <a href="/#showcase" onClick={() => navigate('/#showcase')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Interactive Preview</a>
                    <a href="/#who-is-it-for" onClick={() => navigate('/#who-is-it-for')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Target Audience</a>
                    <a href="/#pricing" onClick={() => navigate('/#pricing')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Pricing</a>
                    <a href="/#faq" onClick={() => navigate('/#faq')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>FAQ</a>
                </nav>

                <div className="header-actions" style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                    <a href="/" onClick={() => navigate('/')} style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                        color: 'white',
                        padding: '0.65rem 1.35rem',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                        transition: 'all 0.2s'
                    }}>
                        Back to Home
                    </a>
                </div>

                {/* Hamburger Toggle Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="hamburger-button"
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        color: '#0f172a',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {isMobileMenuOpen ? (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    )}
                </button>
            </header>

            {/* Mobile Navigation Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{
                            background: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                            position: 'fixed',
                            top: '64px',
                            left: 0,
                            right: 0,
                            zIndex: 999,
                            overflow: 'hidden',
                            boxShadow: '0 15px 30px rgba(0, 0, 0, 0.05)'
                        }}
                        className="mobile-menu-drawer"
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '1.5rem',
                            gap: '1.25rem'
                        }}>
                            <a href="/" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Home</a>
                            <a href="/#features" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#features'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Features</a>
                            <a href="/#showcase" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#showcase'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Interactive Preview</a>
                            <a href="/#who-is-it-for" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#who-is-it-for'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Target Audience</a>
                            <a href="/#pricing" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#pricing'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Pricing</a>
                            <a href="/#faq" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#faq'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>FAQ</a>
                            <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '0.25rem 0' }} />
                            <a href="/support" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/support'); }} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Support Contact</a>
                            <a href="/privacy" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/privacy'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Privacy Policy</a>
                            <a href="/terms" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/terms'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Terms of Service</a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Content */}
            <main style={{ flex: 1, padding: '4rem 2rem', maxWidth: '1000px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                {/* Title Section */}
                <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                    <span style={{
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: '#6366f1',
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        display: 'block',
                        marginBottom: '0.75rem'
                    }}>
                        Get in Touch
                    </span>
                    <h1 style={{
                        fontSize: '2.8rem',
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        color: '#0f172a',
                        margin: 0,
                        lineHeight: 1.15
                    }}>
                        Support Contact
                    </h1>
                    <p style={{
                        fontSize: '1.2rem',
                        color: '#64748b',
                        marginTop: '0.8rem',
                        fontWeight: 500
                    }}>
                        Tvora: Analytics & Profit Support & Merchant Success
                    </p>
                </div>

                {/* Text Content in Glassmorphic Cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <div className="glass-card" style={{
                        padding: '2.5rem',
                        background: 'rgba(255, 255, 255, 0.65)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        borderRadius: '24px',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)',
                        lineHeight: '1.7'
                    }}>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            At <strong>Tvora: Analytics & Profit</strong>, we believe that great software should always come with reliable and responsive support. Whether you are setting up your store analytics for the first time, syncing data, managing customer intelligence, or exploring advanced reporting features, our support team is here to help you at every stage.
                        </p>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            We understand how important accurate business data is for Shopify store owners. That’s why our support approach is designed to be simple, fast, and genuinely helpful. Instead of automated replies that feel robotic, we focus on providing real assistance with practical solutions that help you continue running your business smoothly.
                        </p>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            Our team can assist you with everything related to the Tvora platform, including dashboard setup, store synchronization, analytics configuration, segmentation filters, order intelligence, customer insights, inventory analytics, exports, reporting, and account settings. If you ever face an issue or have questions about a feature, you can simply reach out to us and we’ll guide you through the process step by step.
                        </p>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            Tvora is built for modern Shopify brands, and we know that every store operates differently. Some merchants need help understanding profitability metrics, while others may want assistance with customer segmentation, inventory tracking, or syncing real-time store data. No matter the requirement, our goal is to make sure you get the maximum value from the platform without unnecessary delays or confusion.
                        </p>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            We also continuously improve the app based on user feedback. Many of our features and workflow improvements come directly from conversations with merchants using Tvora daily. If you have suggestions, feature requests, or ideas that could improve your experience, we are always happy to hear them. Your feedback helps us build a smarter and more powerful analytics platform for the Shopify ecosystem.
                        </p>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            Our support team aims to respond as quickly as possible during working hours. For most general inquiries, setup questions, or technical issues, you can expect timely assistance from our team. We always try to provide clear explanations and easy-to-follow solutions so you can focus more on growing your business and less on troubleshooting.
                        </p>
                        <p style={{ margin: '0', fontSize: '1.05rem', color: '#334155' }}>
                            For urgent concerns related to store data, syncing, analytics visibility, exports, or reporting accuracy, please include detailed information in your message such as screenshots, store details, or a short explanation of the issue. This helps our team investigate faster and provide a more accurate solution.
                        </p>
                    </div>

                    {/* Quick Guide Grids */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '2rem'
                    }}>
                        {/* Modules We Help With */}
                        <div className="glass-card" style={{
                            padding: '2.25rem',
                            background: 'rgba(255, 255, 255, 0.65)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.6)',
                            borderRadius: '24px',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)'
                        }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 1.25rem 0' }}>
                                Modules We Support
                            </h3>
                            <p style={{ fontSize: '0.92rem', color: '#64748b', margin: '0 0 1.25rem 0', lineHeight: 1.5 }}>
                                If you are exploring Tvora for the first time, we can help you understand how these modules work:
                            </p>
                            <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {modules.map((mod, i) => (
                                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.95rem', color: '#334155', fontWeight: 500 }}>
                                        <span style={{ color: '#6366f1', fontSize: '1.1rem' }}>✓</span> {mod}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Reasons to Contact Support */}
                        <div className="glass-card" style={{
                            padding: '2.25rem',
                            background: 'rgba(255, 255, 255, 0.65)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.6)',
                            borderRadius: '24px',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)'
                        }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 1.25rem 0' }}>
                                Contact Us For
                            </h3>
                            <p style={{ fontSize: '0.92rem', color: '#64748b', margin: '0 0 1.25rem 0', lineHeight: 1.5 }}>
                                You can contact the Tvora support team for assistance with any of the following areas:
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                {contactReasons.map((reason, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', fontSize: '0.88rem', color: '#334155', fontWeight: 500, lineHeight: 1.3 }}>
                                        <span style={{ color: '#3b82f6' }}>•</span> {reason}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Guidance Card */}
                    <div className="glass-card" style={{
                        padding: '2.5rem',
                        background: 'rgba(255, 255, 255, 0.65)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        borderRadius: '24px',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)',
                        lineHeight: '1.7'
                    }}>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            We recommend checking your store synchronization status regularly to ensure all analytics and intelligence modules are updated properly. If your data appears delayed or incomplete, our support team can quickly help diagnose and resolve the issue.
                        </p>
                        <p style={{ margin: '0 0 1.5rem 0', fontSize: '1.05rem', color: '#334155' }}>
                            At Tvora, support is not just about solving problems — it’s about helping merchants succeed with better insights, better reporting, and smarter business decisions. We are committed to creating a platform experience that feels professional, reliable, and easy to use from day one.
                        </p>
                        <p style={{ margin: '0', fontSize: '1.05rem', color: '#334155' }}>
                            If you need assistance, feel free to reach out to our support team through the available contact channels inside the application or website. We are always ready to help you get the best possible experience with Tvora: Analytics & Profit.
                        </p>
                    </div>

                    {/* Call to Action email box */}
                    <div style={{
                        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
                        borderRadius: '24px',
                        padding: '3rem',
                        textAlign: 'center',
                        color: 'white',
                        boxShadow: '0 20px 50px rgba(15, 23, 42, 0.15)',
                        marginTop: '1.5rem'
                    }}>
                        <h2 style={{ fontSize: '1.8rem', fontWeight: 800, margin: '0 0 0.75rem 0', letterSpacing: '-0.02em' }}>
                            Need Direct Support?
                        </h2>
                        <p style={{ color: '#94a3b8', fontSize: '1.05rem', margin: '0 0 1.75rem 0', maxWidth: '600px', marginInline: 'auto' }}>
                            Our support experts are ready to assist you. Send us an email and we will get back to you as soon as possible.
                        </p>
                        <a href="mailto:support@tvora.co" style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                            color: 'white',
                            textDecoration: 'none',
                            padding: '0.85rem 2rem',
                            borderRadius: '12px',
                            fontWeight: 600,
                            fontSize: '1rem',
                            boxShadow: '0 10px 25px rgba(99, 102, 241, 0.35)',
                            transition: 'all 0.2s'
                        }}>
                            <span>✉</span> Email support@tvora.co
                        </a>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer style={{
                background: '#0f172a',
                color: '#94a3b8',
                padding: '4rem 2rem 2.5rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                marginTop: '4rem'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '3rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '320px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
                                <img src={logoImg} alt="Tvora" style={{ width: '32px', height: '32px', borderRadius: '8px' }} />
                                <span style={{ fontWeight: 800, fontSize: '1.25rem', color: 'white', letterSpacing: '-0.02em' }}>Tvora</span>
                            </div>
                            <p style={{ fontSize: '0.85rem', lineHeight: 1.6, margin: 0 }}>
                                Advanced business intelligence, real-time sync, customer retention modeling, and net profit calculations for growth-minded Shopify store owners.
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '4rem', flexWrap: 'wrap' }}>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Product</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="/#features" onClick={() => navigate('/#features')} style={{ color: '#64748b', textDecoration: 'none' }}>Features</a></li>
                                    <li><a href="/#showcase" onClick={() => navigate('/#showcase')} style={{ color: '#64748b', textDecoration: 'none' }}>Dashboard Showcase</a></li>
                                    <li><a href="/#pricing" onClick={() => navigate('/#pricing')} style={{ color: '#64748b', textDecoration: 'none' }}>Pricing Plans</a></li>
                                </ul>
                            </div>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Resources</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="/#faq" onClick={() => navigate('/#faq')} style={{ color: '#64748b', textDecoration: 'none' }}>FAQ</a></li>
                                    <li><a href="/support" onClick={() => navigate('/support')} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>Support Contact</a></li>
                                    <li><a href="/privacy" onClick={() => navigate('/privacy')} style={{ color: '#64748b', textDecoration: 'none' }}>Privacy Policy</a></li>
                                    <li><a href="/terms" onClick={() => navigate('/terms')} style={{ color: '#64748b', textDecoration: 'none' }}>Terms of Service</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '2rem', fontSize: '0.82rem' }}>
                        <span>© {new Date().getFullYear()} Tvora: Analytics & Profit. All rights reserved.</span>
                        <span>Need assistance? Contact <a href="mailto:support@tvora.co" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>support@tvora.co</a></span>
                    </div>
                </div>
            </footer>

            {/* Mobile/Desktop styling overrides */}
            <style dangerouslySetInnerHTML={{__html: `
                .hamburger-button {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    main {
                        padding: 2.5rem 1.25rem !important;
                    }
                    h1 {
                        font-size: 2rem !important;
                    }
                    .glass-card {
                        padding: 1.5rem !important;
                    }
                    .nav-desktop, .header-actions {
                        display: none !important;
                    }
                    .landing-header .hamburger-button {
                        display: flex !important;
                        margin-left: auto !important;
                    }
                    .mobile-menu-drawer {
                        top: 64px !important;
                    }
                }
            `}} />
        </div>
    );
};
